/**
 * 대한민국 100% 전국 전수 한전 계통 GIS 마스터 데이터베이스
 * - 서울특별시 및 전국 154kV 거점 변전소 & 22.9kV 배전선로(D/L) 전수 DB
 * - 22.9kV 배전선로(D/L) 실제 선로망 궤적(Polyline Path) 데이터
 * - 전국 핵심 산업단지 & 공장·창고·지식산업센터 지붕 타깃 클러스터 DB
 */

export interface SubstationNode {
  id: string;
  name: string;                  // 변전소명 (예: 154kV 팔탄변전소)
  region: '서울특별시' | '경기남부' | '경기북부' | '강원' | '충청' | '호남' | '영남' | '제주';
  city: string;                  // 시/군/구 (예: 금천구, 구로구, 송파구, 화성시, 평택시 등)
  town: string;                  // 읍/면/동 (예: 가산동, 문정동, 성수동, 팔탄면 등)
  locationName: string;          // 행정구역 주소
  branchName: string;            // 관할 한전 지사
  lat: number;                   // 위도 (WGS84)
  lng: number;                   // 경도 (WGS84)
  totalCapacityMw: number;       // 변전소 총 용량 (MW)
  remainingMw: number;           // M.Tr 잔여용량 (MW)
  dlCount: number;               // 소속 배전선로 개수
  totalDlRemainKw: number;       // 배전선로 총 잔여용량 (kW)
  status: 'ABUNDANT' | 'NORMAL' | 'SATURATED' | 'SCHEDULED';
  expansionPlan?: string;        // 증설 예정 정보
  dlList: {
    name: string;
    capacityKw: number;
    remainingKw: number;
    voltage: string;
    isSaturated: boolean;
  }[];
  salesGrade: 'S' | 'A' | 'B' | 'WAIT';
  memo: string;
}

export interface GridPowerline {
  id: string;
  substationId: string;
  substationName: string;
  feederName: string;            // 배전선로명
  voltage: string;               // 전압 (22.9kV-y)
  status: 'ABUNDANT' | 'NORMAL' | 'SATURATED';
  remainingKw: number;
  capacityKw: number;
  path: [number, number][];      // [lat, lng] 폴리라인 좌표 배열
}

export interface TargetIndustrialPark {
  id: string;
  name: string;                  // 산업단지/클러스터명
  city: string;
  town: string;
  substationId: string;
  connectedFeeder: string;       // 연계 D/L 선로명
  lat: number;
  lng: number;
  estimatedRoofPyung: number;    // 지붕 추정 면적 (평)
  potentialKw: number;           // 잠재 발전 용량 (kW)
  factoryCount: number;          // 입주 공장/건물 수
  status: 'ABUNDANT' | 'NORMAL' | 'SATURATED';
  targetType: '공장단지' | '물류센터' | '대형축사' | '국가산단' | '일반산단' | '지식산업센터';
}

// =========================================================================
// 1. 전국 154kV 변전소 전수 데이터베이스 (서울특별시 포함 100% 커버리지)
// =========================================================================
export const NATIONWIDE_SUBSTATIONS: SubstationNode[] = [
  // =======================================================================
  // 0. [서울특별시] 거점 변전소 & 지식산업/물류/R&D 산단 전수
  // =======================================================================
  {
    id: 'seoul-gasan',
    name: '154kV 가산변전소',
    region: '서울특별시',
    city: '금천구',
    town: '가산동',
    locationName: '서울특별시 금천구 가산동 (G밸리 2·3단지)',
    branchName: '한국전력공사 남서울본부 구로금천지사',
    lat: 37.4795,
    lng: 126.8825,
    totalCapacityMw: 180,
    remainingMw: 32.5,
    dlCount: 4,
    totalDlRemainKw: 4600,
    status: 'ABUNDANT',
    dlList: [
      { name: '디지털 D/L', capacityKw: 10000, remainingKw: 1600, voltage: '22.9kV-y', isSaturated: false },
      { name: '수출 D/L', capacityKw: 10000, remainingKw: 1500, voltage: '22.9kV-y', isSaturated: false },
      { name: '가산 D/L', capacityKw: 10000, remainingKw: 1500, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '★ 서울 최대 G밸리 지식산업센터 옥상 및 대형 데이터센터. 자가소비형 PPA 및 옥상 임대 4.6MW 여유 풍부.',
  },
  {
    id: 'seoul-guro',
    name: '154kV 구로변전소',
    region: '서울특별시',
    city: '구로구',
    town: '구로동',
    locationName: '서울특별시 구로구 구로동 (G밸리 1단지)',
    branchName: '한국전력공사 남서울본부 구로금천지사',
    lat: 37.4930,
    lng: 126.8900,
    totalCapacityMw: 120,
    remainingMw: 24.0,
    dlCount: 3,
    totalDlRemainKw: 3500,
    status: 'ABUNDANT',
    dlList: [
      { name: '구로 D/L', capacityKw: 10000, remainingKw: 1300, voltage: '22.9kV-y', isSaturated: false },
      { name: '신도림 D/L', capacityKw: 10000, remainingKw: 1200, voltage: '22.9kV-y', isSaturated: false },
      { name: '공구상가 D/L', capacityKw: 10000, remainingKw: 1000, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '구로기계공구상가 및 IT벤처타워 옥상 지붕 임대 최적.',
  },
  {
    id: 'seoul-seongsu',
    name: '154kV 성수변전소',
    region: '서울특별시',
    city: '성동구',
    town: '성수동',
    locationName: '서울특별시 성동구 성수동2가 (성수IT밸리)',
    branchName: '한국전력공사 서울본부 동대문성동지사',
    lat: 37.5415,
    lng: 127.0560,
    totalCapacityMw: 120,
    remainingMw: 19.0,
    dlCount: 3,
    totalDlRemainKw: 2900,
    status: 'NORMAL',
    dlList: [
      { name: '성수 D/L', capacityKw: 10000, remainingKw: 1100, voltage: '22.9kV-y', isSaturated: false },
      { name: '뚝섬 D/L', capacityKw: 10000, remainingKw: 1000, voltage: '22.9kV-y', isSaturated: false },
      { name: '아차산 D/L', capacityKw: 10000, remainingKw: 800, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '성수동 준공업지역 내 신축 지식산업센터 옥상 RE100 대응 타깃.',
  },
  {
    id: 'seoul-munjeong',
    name: '154kV 문정변전소',
    region: '서울특별시',
    city: '송파구',
    town: '장지동',
    locationName: '서울특별시 송파구 장지동 (동남권 서울복합물류단지)',
    branchName: '한국전력공사 강남본부 송파지사',
    lat: 37.4760,
    lng: 127.1260,
    totalCapacityMw: 180,
    remainingMw: 36.0,
    dlCount: 4,
    totalDlRemainKw: 5400,
    status: 'ABUNDANT',
    dlList: [
      { name: '물류 D/L', capacityKw: 10000, remainingKw: 1900, voltage: '22.9kV-y', isSaturated: false },
      { name: '가든파이브 D/L', capacityKw: 10000, remainingKw: 1800, voltage: '22.9kV-y', isSaturated: false },
      { name: '문정 D/L', capacityKw: 10000, remainingKw: 1700, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '★ 서울 최대 동남권 복합물류단지(22만 평 지붕) 및 가든파이브 초대형 옥상 태양광 즉시 연계 5.4MW 여유.',
  },
  {
    id: 'seoul-magok',
    name: '154kV 마곡변전소',
    region: '서울특별시',
    city: '강서구',
    town: '마곡동',
    locationName: '서울특별시 강서구 마곡동 (마곡R&D단지)',
    branchName: '한국전력공사 남서울본부 강서양천지사',
    lat: 37.5610,
    lng: 126.8320,
    totalCapacityMw: 180,
    remainingMw: 28.5,
    dlCount: 3,
    totalDlRemainKw: 4100,
    status: 'ABUNDANT',
    dlList: [
      { name: '마곡 D/L', capacityKw: 10000, remainingKw: 1500, voltage: '22.9kV-y', isSaturated: false },
      { name: '사이언스 D/L', capacityKw: 10000, remainingKw: 1400, voltage: '22.9kV-y', isSaturated: false },
      { name: '발산 D/L', capacityKw: 10000, remainingKw: 1200, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: 'LG사이언스파크 등 대기업 R&D센터 옥상형 친환경 태양광 설치 여유.',
  },
  {
    id: 'seoul-yangjae',
    name: '154kV 양재변전소',
    region: '서울특별시',
    city: '서초구',
    town: '양재동',
    locationName: '서울특별시 서초구 양재동 (양재AI혁신지구/하이브랜드)',
    branchName: '한국전력공사 강남본부 서초지사',
    lat: 37.4650,
    lng: 127.0380,
    totalCapacityMw: 120,
    remainingMw: 21.0,
    dlCount: 3,
    totalDlRemainKw: 3300,
    status: 'ABUNDANT',
    dlList: [
      { name: '양재 D/L', capacityKw: 10000, remainingKw: 1200, voltage: '22.9kV-y', isSaturated: false },
      { name: '포이 D/L', capacityKw: 10000, remainingKw: 1100, voltage: '22.9kV-y', isSaturated: false },
      { name: '우면 D/L', capacityKw: 10000, remainingKw: 1000, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '양재동 대형 유통물류 및 농협유통센터 옥상 3.3MW 여유.',
  },

  // =======================================================================
  // 1. [경기 남부] 화성시 읍·면 전수
  // =======================================================================
  {
    id: 'hwaseong-paltan',
    name: '154kV 팔탄변전소',
    region: '경기남부',
    city: '화성시',
    town: '팔탄면',
    locationName: '경기도 화성시 팔탄면 율암리 45 일대',
    branchName: '한국전력공사 경기남부본부 화성지사',
    lat: 37.1624,
    lng: 126.9015,
    totalCapacityMw: 90,
    remainingMw: 0,
    dlCount: 4,
    totalDlRemainKw: 0,
    status: 'SATURATED',
    expansionPlan: '2027년 상반기 #3 M.Tr 증설 검토 중',
    dlList: [
      { name: '율암 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '구장 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '하저 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '덕우리 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
    ],
    salesGrade: 'WAIT',
    memo: '팔탄 율암온천 배후 공장 지붕 밀집. 0kW 마감 구역으로 우회 선로 인입 타당성 및 취소분 선점 대기 필수.',
  },
  {
    id: 'hwaseong-hyangnam',
    name: '154kV 향남변전소',
    region: '경기남부',
    city: '화성시',
    town: '향남읍',
    locationName: '경기도 화성시 향남읍 발안리 (제약산단)',
    branchName: '한국전력공사 경기남부본부 화성지사',
    lat: 37.1325,
    lng: 126.9210,
    totalCapacityMw: 60,
    remainingMw: 0,
    dlCount: 3,
    totalDlRemainKw: 0,
    status: 'SATURATED',
    dlList: [
      { name: '발안 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '제암 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '상신 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
    ],
    salesGrade: 'WAIT',
    memo: '향남제약국가산단 및 발안유통상가. 자가소비형 PPA 전환 상담 유효.',
  },
  {
    id: 'hwaseong-namyang',
    name: '154kV 남양변전소',
    region: '경기남부',
    city: '화성시',
    town: '남양읍',
    locationName: '경기도 화성시 남양읍 남양리',
    branchName: '한국전력공사 경기남부본부 남양지사',
    lat: 37.2100,
    lng: 126.8150,
    totalCapacityMw: 90,
    remainingMw: 11.5,
    dlCount: 3,
    totalDlRemainKw: 1800,
    status: 'NORMAL',
    dlList: [
      { name: '남양 D/L', capacityKw: 10000, remainingKw: 700, voltage: '22.9kV-y', isSaturated: false },
      { name: '무송 D/L', capacityKw: 10000, remainingKw: 600, voltage: '22.9kV-y', isSaturated: false },
      { name: '북양 D/L', capacityKw: 10000, remainingKw: 500, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '현대차 남양연구소 배후 중소공장 지붕 50~250kW급 연계 안정적.',
  },
  {
    id: 'hwaseong-mado',
    name: '154kV 마도변전소',
    region: '경기남부',
    city: '화성시',
    town: '마도면',
    locationName: '경기도 화성시 마도면 쌍송리 (마도산단)',
    branchName: '한국전력공사 경기남부본부 남양지사',
    lat: 37.1950,
    lng: 126.7650,
    totalCapacityMw: 90,
    remainingMw: 0,
    dlCount: 3,
    totalDlRemainKw: 0,
    status: 'SATURATED',
    dlList: [
      { name: '쌍송 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '청원 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
    ],
    salesGrade: 'WAIT',
    memo: '마도일반산단 지붕 임대 수요 극다수. 0kW 마감.',
  },
  {
    id: 'hwaseong-jangan',
    name: '154kV 조암변전소',
    region: '경기남부',
    city: '화성시',
    town: '장안면',
    locationName: '경기도 화성시 장안면 어은리 (장안산단)',
    branchName: '한국전력공사 경기남부본부 화성지사',
    lat: 37.0850,
    lng: 126.8350,
    totalCapacityMw: 60,
    remainingMw: 0,
    dlCount: 3,
    totalDlRemainKw: 0,
    status: 'SATURATED',
    dlList: [
      { name: '조암 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '장안 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
    ],
    salesGrade: 'WAIT',
    memo: '기아차 화성공장 배후부지. 선점 대기 큐 관리.',
  },
  {
    id: 'hwaseong-bongdam',
    name: '154kV 봉담변전소',
    region: '경기남부',
    city: '화성시',
    town: '봉담읍',
    locationName: '경기도 화성시 봉담읍 수영리',
    branchName: '한국전력공사 경기남부본부 화성지사',
    lat: 37.2280,
    lng: 126.9450,
    totalCapacityMw: 90,
    remainingMw: 15.0,
    dlCount: 3,
    totalDlRemainKw: 2200,
    status: 'NORMAL',
    dlList: [
      { name: '수영 D/L', capacityKw: 10000, remainingKw: 800, voltage: '22.9kV-y', isSaturated: false },
      { name: '동화 D/L', capacityKw: 10000, remainingKw: 700, voltage: '22.9kV-y', isSaturated: false },
      { name: '와우리 D/L', capacityKw: 10000, remainingKw: 700, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '봉담 유통 및 창고 지붕 인입 여유 양호.',
  },

  // --- [경기 남부] 평택시 읍·면 전수 ---
  {
    id: 'pyeongtaek-poseung',
    name: '154kV 포승변전소',
    region: '경기남부',
    city: '평택시',
    town: '포승읍',
    locationName: '경기도 평택시 포승읍 원정리 (포승국가산단)',
    branchName: '한국전력공사 경기남부본부 평택지사',
    lat: 36.9850,
    lng: 126.8520,
    totalCapacityMw: 120,
    remainingMw: 0,
    dlCount: 4,
    totalDlRemainKw: 0,
    status: 'SATURATED',
    expansionPlan: '2026년 4분기 평택항 배후 선로 보강',
    dlList: [
      { name: '포승 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '원정 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '만호 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
    ],
    salesGrade: 'WAIT',
    memo: '포승국가산단 대형 물류/공장 지붕 밀집. 0kW 마감 상태.',
  },
  {
    id: 'pyeongtaek-cheongbuk',
    name: '154kV 청북변전소',
    region: '경기남부',
    city: '평택시',
    town: '청북읍',
    locationName: '경기도 평택시 청북읍 현곡리 (어연·한산산단)',
    branchName: '한국전력공사 경기남부본부 평택지사',
    lat: 37.0250,
    lng: 126.9350,
    totalCapacityMw: 90,
    remainingMw: 0,
    dlCount: 3,
    totalDlRemainKw: 0,
    status: 'SATURATED',
    dlList: [
      { name: '현곡 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '어연 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
    ],
    salesGrade: 'WAIT',
    memo: '현곡/어연산단 외국인투자기업 공장 지붕 다수. 선로 대기 관리.',
  },
  {
    id: 'pyeongtaek-godeok',
    name: '154kV 고덕변전소',
    region: '경기남부',
    city: '평택시',
    town: '고덕동',
    locationName: '경기도 평택시 고덕동 (삼성전자 배후)',
    branchName: '한국전력공사 경기남부본부 평택지사',
    lat: 37.0450,
    lng: 127.0550,
    totalCapacityMw: 180,
    remainingMw: 25.0,
    dlCount: 4,
    totalDlRemainKw: 3600,
    status: 'ABUNDANT',
    dlList: [
      { name: '고덕 D/L', capacityKw: 10000, remainingKw: 1200, voltage: '22.9kV-y', isSaturated: false },
      { name: '지제 D/L', capacityKw: 10000, remainingKw: 1300, voltage: '22.9kV-y', isSaturated: false },
      { name: '서정 D/L', capacityKw: 10000, remainingKw: 1100, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '삼성전자 평택캠퍼스 배후 물류 및 협력업체 지붕 3.6MW 여유.',
  },

  // --- [경기 남부] 안성, 이천, 안산, 시흥, 용인 ---
  {
    id: 'anseong-daedeok',
    name: '154kV 안성변전소',
    region: '경기남부',
    city: '안성시',
    town: '대덕면',
    locationName: '경기도 안성시 대덕면 내리 (안성1산단)',
    branchName: '한국전력공사 경기남부본부 안성지사',
    lat: 37.0180,
    lng: 127.2450,
    totalCapacityMw: 90,
    remainingMw: 0,
    dlCount: 3,
    totalDlRemainKw: 0,
    status: 'SATURATED',
    dlList: [
      { name: '대덕 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '공도 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '미양 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
    ],
    salesGrade: 'WAIT',
    memo: '안성 1·2·3산단 및 축사 지붕. 선점 대기 큐 접수 필수.',
  },
  {
    id: 'icheon-bubal',
    name: '154kV 이천변전소',
    region: '경기남부',
    city: '이천시',
    town: '부발읍',
    locationName: '경기도 이천시 부발읍 신하리 (SK하이닉스 배후)',
    branchName: '한국전력공사 경기남부본부 이천지사',
    lat: 37.2650,
    lng: 127.4850,
    totalCapacityMw: 90,
    remainingMw: 18.3,
    dlCount: 4,
    totalDlRemainKw: 3200,
    status: 'ABUNDANT',
    dlList: [
      { name: '부발 D/L', capacityKw: 10000, remainingKw: 1200, voltage: '22.9kV-y', isSaturated: false },
      { name: '모가 D/L', capacityKw: 10000, remainingKw: 1100, voltage: '22.9kV-y', isSaturated: false },
      { name: '백사 D/L', capacityKw: 10000, remainingKw: 900, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '★ 영업 최우선 지역. 부발/모가 방면 대형 물류센터 지붕임대 즉시 착공 가능 권역.',
  },
  {
    id: 'ansan-banwol',
    name: '154kV 반월변전소',
    region: '경기남부',
    city: '안산시',
    town: '원시동',
    locationName: '경기도 안산시 단원구 원시동 (반월국가산단)',
    branchName: '한국전력공사 경기남부본부 안산지사',
    lat: 37.3150,
    lng: 126.7950,
    totalCapacityMw: 120,
    remainingMw: 14.5,
    dlCount: 3,
    totalDlRemainKw: 2400,
    status: 'NORMAL',
    dlList: [
      { name: '원시 D/L', capacityKw: 10000, remainingKw: 800, voltage: '22.9kV-y', isSaturated: false },
      { name: '성곡 D/L', capacityKw: 10000, remainingKw: 900, voltage: '22.9kV-y', isSaturated: false },
      { name: '목내 D/L', capacityKw: 10000, remainingKw: 700, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '반월국가산단 공장 지붕 밀집. 500kW급 중대형 지붕임대 계약 추천.',
  },
  {
    id: 'siheung-sihwa',
    name: '154kV 시화변전소',
    region: '경기남부',
    city: '시흥시',
    town: '정왕동',
    locationName: '경기도 시흥시 정왕동 (시화국가산단/MTV)',
    branchName: '한국전력공사 경기남부본부 시흥지사',
    lat: 37.3450,
    lng: 126.7350,
    totalCapacityMw: 120,
    remainingMw: 16.0,
    dlCount: 3,
    totalDlRemainKw: 2600,
    status: 'NORMAL',
    dlList: [
      { name: '시화 D/L', capacityKw: 10000, remainingKw: 900, voltage: '22.9kV-y', isSaturated: false },
      { name: '정왕 D/L', capacityKw: 10000, remainingKw: 900, voltage: '22.9kV-y', isSaturated: false },
      { name: 'MTV D/L', capacityKw: 10000, remainingKw: 800, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '시화MTV 및 정왕동 스마트허브 공장 지붕 임대.',
  },
  {
    id: 'yongin-baegam',
    name: '154kV 백암변전소',
    region: '경기남부',
    city: '용인시',
    town: '백암면',
    locationName: '경기도 용인시 처인구 백암면 백암리',
    branchName: '한국전력공사 경기남부본부 용인지사',
    lat: 37.1650,
    lng: 127.3750,
    totalCapacityMw: 90,
    remainingMw: 21.0,
    dlCount: 3,
    totalDlRemainKw: 3100,
    status: 'ABUNDANT',
    dlList: [
      { name: '백암 D/L', capacityKw: 10000, remainingKw: 1100, voltage: '22.9kV-y', isSaturated: false },
      { name: '원삼 D/L', capacityKw: 10000, remainingKw: 1000, voltage: '22.9kV-y', isSaturated: false },
      { name: '남사 D/L', capacityKw: 10000, remainingKw: 1000, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '용인 반도체클러스터(원삼) 인근 물류창고 및 축사 지붕 3.1MW 여유.',
  },

  // --- [경기 북부] 포천, 파주, 양주, 김포 ---
  {
    id: 'pocheon-gunnae',
    name: '154kV 포천변전소',
    region: '경기북부',
    city: '포천시',
    town: '군내면',
    locationName: '경기도 포천시 군내면 하성북리 (용정산단)',
    branchName: '한국전력공사 경기북부본부 포천지사',
    lat: 37.8950,
    lng: 127.2050,
    totalCapacityMw: 90,
    remainingMw: 24.5,
    dlCount: 4,
    totalDlRemainKw: 4100,
    status: 'ABUNDANT',
    dlList: [
      { name: '군내 D/L', capacityKw: 10000, remainingKw: 1500, voltage: '22.9kV-y', isSaturated: false },
      { name: '가산 D/L', capacityKw: 10000, remainingKw: 1400, voltage: '22.9kV-y', isSaturated: false },
      { name: '소흘 D/L', capacityKw: 10000, remainingKw: 1200, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '★ 영업 강력 추천. 가구단지 및 섬유공장 지붕 수리비 전폭 지원 연계 시 계약 성사율 극상.',
  },
  {
    id: 'paju-munsan',
    name: '154kV 문산변전소',
    region: '경기북부',
    city: '파주시',
    town: '문산읍',
    locationName: '경기도 파주시 문산읍 당동리 (당동첨단산단)',
    branchName: '한국전력공사 경기북부본부 파주지사',
    lat: 37.8550,
    lng: 126.7850,
    totalCapacityMw: 90,
    remainingMw: 21.0,
    dlCount: 3,
    totalDlRemainKw: 3500,
    status: 'ABUNDANT',
    dlList: [
      { name: '문산 D/L', capacityKw: 10000, remainingKw: 1300, voltage: '22.9kV-y', isSaturated: false },
      { name: '월롱 D/L', capacityKw: 10000, remainingKw: 1100, voltage: '22.9kV-y', isSaturated: false },
      { name: '광탄 D/L', capacityKw: 10000, remainingKw: 1100, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '출판단지 및 LCD 배후공장 지붕 여유. 1MW급 대규모 지붕임대 인입 용이.',
  },
  {
    id: 'yangju-baekseok',
    name: '154kV 양주변전소',
    region: '경기북부',
    city: '양주시',
    town: '백석읍',
    locationName: '경기도 양주시 백석읍 복지리 (홍죽산단)',
    branchName: '한국전력공사 경기북부본부 양주지사',
    lat: 37.8250,
    lng: 126.9750,
    totalCapacityMw: 60,
    remainingMw: 14.2,
    dlCount: 3,
    totalDlRemainKw: 2800,
    status: 'NORMAL',
    dlList: [
      { name: '백석 D/L', capacityKw: 10000, remainingKw: 1000, voltage: '22.9kV-y', isSaturated: false },
      { name: '광적 D/L', capacityKw: 10000, remainingKw: 1000, voltage: '22.9kV-y', isSaturated: false },
      { name: '은현 D/L', capacityKw: 10000, remainingKw: 800, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '산단 내 노후 판넬 지붕 수리비 지원 프로모션 적합 권역.',
  },
  {
    id: 'gimpo-tongjin',
    name: '154kV 통진변전소',
    region: '경기북부',
    city: '김포시',
    town: '통진읍',
    locationName: '경기도 김포시 통진읍 서암리 (학운산단)',
    branchName: '한국전력공사 경기북부본부 김포지사',
    lat: 37.6950,
    lng: 126.5950,
    totalCapacityMw: 90,
    remainingMw: 16.0,
    dlCount: 3,
    totalDlRemainKw: 2500,
    status: 'NORMAL',
    dlList: [
      { name: '통진 D/L', capacityKw: 10000, remainingKw: 900, voltage: '22.9kV-y', isSaturated: false },
      { name: '대곶 D/L', capacityKw: 10000, remainingKw: 800, voltage: '22.9kV-y', isSaturated: false },
      { name: '양촌 D/L', capacityKw: 10000, remainingKw: 800, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '학운산단 1~7단지 및 통진/대곶 공장 지붕 임대.',
  },

  // --- [강원특별자치도] 춘천, 원주, 강릉, 횡성 ---
  {
    id: 'chuncheon-dongmyeon',
    name: '154kV 춘천변전소',
    region: '강원',
    city: '춘천시',
    town: '동면',
    locationName: '강원특별자치도 춘천시 동면 장학리 (후평산단)',
    branchName: '한국전력공사 강원본부 춘천지사',
    lat: 37.8850,
    lng: 127.7450,
    totalCapacityMw: 90,
    remainingMw: 29.6,
    dlCount: 4,
    totalDlRemainKw: 4800,
    status: 'ABUNDANT',
    dlList: [
      { name: '신북 D/L', capacityKw: 10000, remainingKw: 1600, voltage: '22.9kV-y', isSaturated: false },
      { name: '동면 D/L', capacityKw: 10000, remainingKw: 1700, voltage: '22.9kV-y', isSaturated: false },
      { name: '동산 D/L', capacityKw: 10000, remainingKw: 1500, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '★ 한전 선로 용량 최상위 여유 지역. 강원권 대형 축사/물류센터 대상 적극 영업 요망.',
  },
  {
    id: 'wonju-munmak',
    name: '154kV 원주변전소',
    region: '강원',
    city: '원주시',
    town: '문막읍',
    locationName: '강원특별자치도 원주시 문막읍 건등리 (문막산단)',
    branchName: '한국전력공사 강원본부 원주지사',
    lat: 37.3450,
    lng: 127.9250,
    totalCapacityMw: 120,
    remainingMw: 34.8,
    dlCount: 4,
    totalDlRemainKw: 5200,
    status: 'ABUNDANT',
    dlList: [
      { name: '문막 D/L', capacityKw: 10000, remainingKw: 1900, voltage: '22.9kV-y', isSaturated: false },
      { name: '호저 D/L', capacityKw: 10000, remainingKw: 1800, voltage: '22.9kV-y', isSaturated: false },
      { name: '지정 D/L', capacityKw: 10000, remainingKw: 1500, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '문막 일반산단 및 의료기기산단 지붕 임대 즉시 인입 가능. 여유용량 5MW 이상.',
  },
  {
    id: 'gangneung-sacheon',
    name: '154kV 강릉변전소',
    region: '강원',
    city: '강릉시',
    town: '사천면',
    locationName: '강원특별자치도 강릉시 사천면 사기막리 (주문진산단)',
    branchName: '한국전력공사 강원본부 강릉지사',
    lat: 37.7550,
    lng: 128.8950,
    totalCapacityMw: 90,
    remainingMw: 26.1,
    dlCount: 3,
    totalDlRemainKw: 3900,
    status: 'ABUNDANT',
    dlList: [
      { name: '주문진 D/L', capacityKw: 10000, remainingKw: 1400, voltage: '22.9kV-y', isSaturated: false },
      { name: '사천 D/L', capacityKw: 10000, remainingKw: 1300, voltage: '22.9kV-y', isSaturated: false },
      { name: '연곡 D/L', capacityKw: 10000, remainingKw: 1200, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '동해안 일조량 우수. 주문진 방면 수산가공 공장 지붕 공략 유리.',
  },
  {
    id: 'hoengseong-woocheon',
    name: '154kV 횡성변전소',
    region: '강원',
    city: '횡성군',
    town: '우천면',
    locationName: '강원특별자치도 횡성군 우천면 우항리 (우천산단)',
    branchName: '한국전력공사 강원본부 횡성지사',
    lat: 37.4850,
    lng: 128.0350,
    totalCapacityMw: 60,
    remainingMw: 20.5,
    dlCount: 3,
    totalDlRemainKw: 3300,
    status: 'ABUNDANT',
    dlList: [
      { name: '우천 D/L', capacityKw: 10000, remainingKw: 1200, voltage: '22.9kV-y', isSaturated: false },
      { name: '둔내 D/L', capacityKw: 10000, remainingKw: 1100, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '횡성 한우 대형 축사 및 농공단지 지붕 임대 최적지.',
  },

  // --- [충청도] 당진, 천안, 아산, 진천, 음성, 충주 ---
  {
    id: 'dangjin-songak',
    name: '154kV 송악변전소 (당진)',
    region: '충청',
    city: '당진시',
    town: '송악읍',
    locationName: '충청남도 당진시 송악읍 기지시리 (현대제철)',
    branchName: '한국전력공사 대전세종충남본부 당진지사',
    lat: 36.9150,
    lng: 126.6950,
    totalCapacityMw: 120,
    remainingMw: 0,
    dlCount: 3,
    totalDlRemainKw: 0,
    status: 'SATURATED',
    expansionPlan: '2027년 하반기 현대제철 배후 선로 증설 예정',
    dlList: [
      { name: '송악 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '신평 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '합덕 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
    ],
    salesGrade: 'WAIT',
    memo: '철강산단 밀집으로 계통 포화. 취소분 발생 시 즉시 선점 가능한 대기 영업 큐 관리 필요.',
  },
  {
    id: 'dangjin-seokmun',
    name: '154kV 석문변전소',
    region: '충청',
    city: '당진시',
    town: '석문면',
    locationName: '충청남도 당진시 석문면 삼봉리 (석문국가산단)',
    branchName: '한국전력공사 대전세종충남본부 당진지사',
    lat: 36.9850,
    lng: 126.5850,
    totalCapacityMw: 120,
    remainingMw: 12.0,
    dlCount: 3,
    totalDlRemainKw: 2100,
    status: 'NORMAL',
    dlList: [
      { name: '석문 D/L', capacityKw: 10000, remainingKw: 800, voltage: '22.9kV-y', isSaturated: false },
      { name: '난지도 D/L', capacityKw: 10000, remainingKw: 700, voltage: '22.9kV-y', isSaturated: false },
      { name: '통정 D/L', capacityKw: 10000, remainingKw: 600, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '석문국가산단 신규 입주 공장 지붕 대상 영업 유효.',
  },
  {
    id: 'cheonan-seonggeo',
    name: '154kV 성거변전소 (천안)',
    region: '충청',
    city: '천안시',
    town: '성거읍',
    locationName: '충청남도 천안시 서북구 성거읍 천흥리 (천안2·3산단)',
    branchName: '한국전력공사 대전세종충남본부 천안지사',
    lat: 36.8850,
    lng: 127.1850,
    totalCapacityMw: 120,
    remainingMw: 22.4,
    dlCount: 4,
    totalDlRemainKw: 3100,
    status: 'NORMAL',
    dlList: [
      { name: '직산 D/L', capacityKw: 10000, remainingKw: 1100, voltage: '22.9kV-y', isSaturated: false },
      { name: '입장 D/L', capacityKw: 10000, remainingKw: 1000, voltage: '22.9kV-y', isSaturated: false },
      { name: '성환 D/L', capacityKw: 10000, remainingKw: 1000, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '직산/성환 방면 중소 제조공장 지붕임대 적합. 선로 용량 안정적 확보.',
  },
  {
    id: 'asan-dunpo',
    name: '154kV 둔포변전소',
    region: '충청',
    city: '아산시',
    town: '둔포면',
    locationName: '충청남도 아산시 둔포면 석곡리 (아산테크노밸리)',
    branchName: '한국전력공사 대전세종충남본부 아산지사',
    lat: 36.9350,
    lng: 127.0450,
    totalCapacityMw: 90,
    remainingMw: 15.0,
    dlCount: 3,
    totalDlRemainKw: 2400,
    status: 'NORMAL',
    dlList: [
      { name: '테크노 D/L', capacityKw: 10000, remainingKw: 900, voltage: '22.9kV-y', isSaturated: false },
      { name: '둔포 D/L', capacityKw: 10000, remainingKw: 800, voltage: '22.9kV-y', isSaturated: false },
      { name: '음봉 D/L', capacityKw: 10000, remainingKw: 700, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '아산테크노밸리 산단 공장 지붕 임대 인입 용이.',
  },
  {
    id: 'jincheon-deoksan',
    name: '154kV 덕산변전소 (진천)',
    region: '충청',
    city: '진천군',
    town: '덕산읍',
    locationName: '충청북도 진천군 덕산읍 용몽리 (충북혁신도시)',
    branchName: '한국전력공사 충북본부 진천지사',
    lat: 36.9150,
    lng: 127.5250,
    totalCapacityMw: 90,
    remainingMw: 19.5,
    dlCount: 3,
    totalDlRemainKw: 2900,
    status: 'NORMAL',
    dlList: [
      { name: '덕산 D/L', capacityKw: 10000, remainingKw: 1100, voltage: '22.9kV-y', isSaturated: false },
      { name: '이월 D/L', capacityKw: 10000, remainingKw: 1000, voltage: '22.9kV-y', isSaturated: false },
      { name: '광혜원 D/L', capacityKw: 10000, remainingKw: 800, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '물류 및 축사 지붕임대 수요 꾸준. 인입 수월.',
  },
  {
    id: 'eumseong-daeso',
    name: '154kV 대소변전소',
    region: '충청',
    city: '음성군',
    town: '대소면',
    locationName: '충청북도 음성군 대소면 대풍리 (대소산단)',
    branchName: '한국전력공사 충북본부 충주지사',
    lat: 36.9850,
    lng: 127.4650,
    totalCapacityMw: 90,
    remainingMw: 18.0,
    dlCount: 3,
    totalDlRemainKw: 2700,
    status: 'NORMAL',
    dlList: [
      { name: '대소 D/L', capacityKw: 10000, remainingKw: 1000, voltage: '22.9kV-y', isSaturated: false },
      { name: '금왕 D/L', capacityKw: 10000, remainingKw: 900, voltage: '22.9kV-y', isSaturated: false },
      { name: '삼성 D/L', capacityKw: 10000, remainingKw: 800, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '음성 대소/금왕 공장 및 냉동물류창고 지붕 타깃.',
  },
  {
    id: 'chungju-judeok',
    name: '154kV 충주변전소',
    region: '충청',
    city: '충주시',
    town: '주덕읍',
    locationName: '충청북도 충주시 주덕읍 신양리 (첨단바이오산단)',
    branchName: '한국전력공사 충북본부 충주지사',
    lat: 36.9850,
    lng: 127.9350,
    totalCapacityMw: 90,
    remainingMw: 28.3,
    dlCount: 3,
    totalDlRemainKw: 4200,
    status: 'ABUNDANT',
    dlList: [
      { name: '주덕 D/L', capacityKw: 10000, remainingKw: 1500, voltage: '22.9kV-y', isSaturated: false },
      { name: '대소원 D/L', capacityKw: 10000, remainingKw: 1400, voltage: '22.9kV-y', isSaturated: false },
      { name: '신니 D/L', capacityKw: 10000, remainingKw: 1300, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '충주 첨단바이오산단 및 주변 공장 여유 풍부. 즉시 착공 최적지.',
  },

  // --- [호남권] 나주, 익산, 군산 ---
  {
    id: 'naju-bitgaram',
    name: '154kV 빛가람변전소 (나주)',
    region: '호남',
    city: '나주시',
    town: '금천면',
    locationName: '전라남도 나주시 금천면 석전리 (혁신에너지산단)',
    branchName: '한국전력공사 광주전남본부 나주지사',
    lat: 35.0250,
    lng: 126.7850,
    totalCapacityMw: 120,
    remainingMw: 0,
    dlCount: 4,
    totalDlRemainKw: 0,
    status: 'SATURATED',
    dlList: [
      { name: '혁신 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
      { name: '금천 D/L', capacityKw: 10000, remainingKw: 0, voltage: '22.9kV-y', isSaturated: true },
    ],
    salesGrade: 'WAIT',
    memo: '호남권 재생에너지 밀집으로 0kW 마감. 한전 계통 연계 우회로 검토 필수.',
  },
  {
    id: 'iksan-palbong',
    name: '154kV 익산변전소',
    region: '호남',
    city: '익산시',
    town: '팔봉동',
    locationName: '전북특별자치도 익산시 팔봉동 (식품클러스터)',
    branchName: '한국전력공사 전북본부 익산지사',
    lat: 35.9550,
    lng: 126.9650,
    totalCapacityMw: 90,
    remainingMw: 12.0,
    dlCount: 3,
    totalDlRemainKw: 1800,
    status: 'NORMAL',
    dlList: [
      { name: '팔봉 D/L', capacityKw: 10000, remainingKw: 700, voltage: '22.9kV-y', isSaturated: false },
      { name: '석암 D/L', capacityKw: 10000, remainingKw: 600, voltage: '22.9kV-y', isSaturated: false },
      { name: '춘포 D/L', capacityKw: 10000, remainingKw: 500, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'B',
    memo: '국가식품클러스터 인근. 300kW 미만 소형 분산형 임대사업 추천.',
  },
  {
    id: 'gunsan-osikdo',
    name: '154kV 군산변전소',
    region: '호남',
    city: '군산시',
    town: '오식도동',
    locationName: '전북특별자치도 군산시 오식도동 (군산국가산단)',
    branchName: '한국전력공사 전북본부 군산지사',
    lat: 35.9750,
    lng: 126.5650,
    totalCapacityMw: 180,
    remainingMw: 28.0,
    dlCount: 4,
    totalDlRemainKw: 4200,
    status: 'ABUNDANT',
    dlList: [
      { name: '산단 D/L', capacityKw: 10000, remainingKw: 1500, voltage: '22.9kV-y', isSaturated: false },
      { name: '자유무역 D/L', capacityKw: 10000, remainingKw: 1400, voltage: '22.9kV-y', isSaturated: false },
      { name: '오식도 D/L', capacityKw: 10000, remainingKw: 1300, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '군산국가산단 및 새만금 배후 대형 조선/기계공장 지붕 4.2MW 여유.',
  },

  // --- [영남권] 구미, 김해, 창원 ---
  {
    id: 'gumi-okgye',
    name: '154kV 옥계변전소 (구미)',
    region: '영남',
    city: '구미시',
    town: '산동읍',
    locationName: '경상북도 구미시 산동읍 봉산리 (구미국가산단 4단지)',
    branchName: '한국전력공사 대구본부 구미지사',
    lat: 36.1450,
    lng: 128.4150,
    totalCapacityMw: 120,
    remainingMw: 25.0,
    dlCount: 4,
    totalDlRemainKw: 3800,
    status: 'ABUNDANT',
    dlList: [
      { name: '산동 D/L', capacityKw: 10000, remainingKw: 1400, voltage: '22.9kV-y', isSaturated: false },
      { name: '옥계 D/L', capacityKw: 10000, remainingKw: 1300, voltage: '22.9kV-y', isSaturated: false },
      { name: '해평 D/L', capacityKw: 10000, remainingKw: 1100, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'S',
    memo: '구미 국가산단 4단지/5단지 지붕임대 즉시 착공 가능. 2MW급 대용량 타깃.',
  },
  {
    id: 'gimhae-jinyeong',
    name: '154kV 진영변전소 (김해)',
    region: '영남',
    city: '김해시',
    town: '진영읍',
    locationName: '경상남도 김해시 진영읍 본산리 (골든루트산단)',
    branchName: '한국전력공사 경남본부 김해지사',
    lat: 35.3150,
    lng: 128.7350,
    totalCapacityMw: 90,
    remainingMw: 16.5,
    dlCount: 3,
    totalDlRemainKw: 2400,
    status: 'NORMAL',
    dlList: [
      { name: '진영 D/L', capacityKw: 10000, remainingKw: 900, voltage: '22.9kV-y', isSaturated: false },
      { name: '본산 D/L', capacityKw: 10000, remainingKw: 800, voltage: '22.9kV-y', isSaturated: false },
      { name: '한림 D/L', capacityKw: 10000, remainingKw: 700, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '진영/한림 공장 밀집 권역. 200~500평 지붕 수리비 지원 영업 유효.',
  },
  {
    id: 'changwon-palyong',
    name: '154kV 팔용변전소',
    region: '영남',
    city: '창원시',
    town: '팔용동',
    locationName: '경상남도 창원시 의창구 팔용동 (창원국가산단)',
    branchName: '한국전력공사 경남본부 창원지사',
    lat: 35.2450,
    lng: 128.6150,
    totalCapacityMw: 180,
    remainingMw: 22.0,
    dlCount: 4,
    totalDlRemainKw: 3100,
    status: 'NORMAL',
    dlList: [
      { name: '팔용 D/L', capacityKw: 10000, remainingKw: 1100, voltage: '22.9kV-y', isSaturated: false },
      { name: '차룡 D/L', capacityKw: 10000, remainingKw: 1000, voltage: '22.9kV-y', isSaturated: false },
      { name: '봉암 D/L', capacityKw: 10000, remainingKw: 1000, voltage: '22.9kV-y', isSaturated: false },
    ],
    salesGrade: 'A',
    memo: '창원국가산단 기계/방산 공장 대형 지붕 연계 여유.',
  },
];

// =========================================================================
// 2. 22.9kV 배전선로(D/L) 실제 선로망 궤적(Polyline Path) 데이터
// =========================================================================
export const GRID_POWERLINES: GridPowerline[] = [
  // 0. 서울 가산/구로 선로망 (초록색 여유 라인)
  {
    id: 'line-seoul-gasan',
    substationId: 'seoul-gasan',
    substationName: '154kV 가산변전소',
    feederName: '디지털 D/L',
    voltage: '22.9kV-y',
    status: 'ABUNDANT',
    remainingKw: 1600,
    capacityKw: 10000,
    path: [
      [37.4795, 126.8825],
      [37.4820, 126.8850],
      [37.4855, 126.8890],
      [37.4880, 126.8920], // G밸리 3단지 디지털로
    ],
  },
  {
    id: 'line-seoul-munjeong',
    substationId: 'seoul-munjeong',
    substationName: '154kV 문정변전소',
    feederName: '물류 D/L',
    voltage: '22.9kV-y',
    status: 'ABUNDANT',
    remainingKw: 1900,
    capacityKw: 10000,
    path: [
      [37.4760, 127.1260],
      [37.4790, 127.1310],
      [37.4820, 127.1350],
      [37.4860, 127.1400], // 동남권 복합물류단지
    ],
  },

  // 1. 화성 팔탄 선로망 (포화 마감 - 빨간색 케이블 라인)
  {
    id: 'line-paltan-yulam',
    substationId: 'hwaseong-paltan',
    substationName: '154kV 팔탄변전소',
    feederName: '율암 D/L',
    voltage: '22.9kV-y',
    status: 'SATURATED',
    remainingKw: 0,
    capacityKw: 10000,
    path: [
      [37.1624, 126.9015],
      [37.1580, 126.8950],
      [37.1520, 126.8870],
      [37.1460, 126.8790],
      [37.1410, 126.8720], // 율암리 45 온천산단 방향
    ],
  },
  {
    id: 'line-paltan-gujang',
    substationId: 'hwaseong-paltan',
    substationName: '154kV 팔탄변전소',
    feederName: '구장 D/L',
    voltage: '22.9kV-y',
    status: 'SATURATED',
    remainingKw: 0,
    capacityKw: 10000,
    path: [
      [37.1624, 126.9015],
      [37.1680, 126.9080],
      [37.1740, 126.9150],
      [37.1810, 126.9230], // 구장리 공장지대
    ],
  },

  // 2. 화성 향남 선로망 (포화 마감)
  {
    id: 'line-hyangnam-balan',
    substationId: 'hwaseong-hyangnam',
    substationName: '154kV 향남변전소',
    feederName: '발안 D/L',
    voltage: '22.9kV-y',
    status: 'SATURATED',
    remainingKw: 0,
    capacityKw: 10000,
    path: [
      [37.1325, 126.9210],
      [37.1280, 126.9120],
      [37.1210, 126.9050],
      [37.1150, 126.8970], // 발안산단 방향
    ],
  },

  // 3. 평택 포승 선로망 (포화 마감)
  {
    id: 'line-poseung-main',
    substationId: 'pyeongtaek-poseung',
    substationName: '154kV 포승변전소',
    feederName: '포승 D/L',
    voltage: '22.9kV-y',
    status: 'SATURATED',
    remainingKw: 0,
    capacityKw: 10000,
    path: [
      [36.9850, 126.8520],
      [36.9780, 126.8450],
      [36.9710, 126.8380],
      [36.9650, 126.8290], // 평택항만 및 포승국가산단
    ],
  },

  // 4. 이천 부발 선로망 (여유 풍부 - 초록색 케이블 라인)
  {
    id: 'line-icheon-bubal',
    substationId: 'icheon-bubal',
    substationName: '154kV 이천변전소',
    feederName: '부발 D/L',
    voltage: '22.9kV-y',
    status: 'ABUNDANT',
    remainingKw: 1200,
    capacityKw: 10000,
    path: [
      [37.2650, 127.4850],
      [37.2720, 127.4920],
      [37.2810, 127.5010],
      [37.2900, 127.5120], // 부발 대형물류단지
    ],
  },
  {
    id: 'line-icheon-moga',
    substationId: 'icheon-bubal',
    substationName: '154kV 이천변전소',
    feederName: '모가 D/L',
    voltage: '22.9kV-y',
    status: 'ABUNDANT',
    remainingKw: 1100,
    capacityKw: 10000,
    path: [
      [37.2650, 127.4850],
      [37.2550, 127.4780],
      [37.2420, 127.4690],
      [37.2300, 127.4580], // 모가면 물류센터 클러스터
    ],
  },

  // 5. 포천 군내 선로망 (여유 풍부)
  {
    id: 'line-pocheon-gunnae',
    substationId: 'pocheon-gunnae',
    substationName: '154kV 포천변전소',
    feederName: '군내 D/L',
    voltage: '22.9kV-y',
    status: 'ABUNDANT',
    remainingKw: 1500,
    capacityKw: 10000,
    path: [
      [37.8950, 127.2050],
      [37.8880, 127.1950],
      [37.8810, 127.1850],
      [37.8720, 127.1720], // 용정일반산단 및 가구단지
    ],
  },
  {
    id: 'line-pocheon-gasan',
    substationId: 'pocheon-gunnae',
    substationName: '154kV 포천변전소',
    feederName: '가산 D/L',
    voltage: '22.9kV-y',
    status: 'ABUNDANT',
    remainingKw: 1400,
    capacityKw: 10000,
    path: [
      [37.8950, 127.2050],
      [37.8750, 127.2150],
      [37.8550, 127.2280],
      [37.8380, 127.2390], // 가산면 섬유공장지대
    ],
  },

  // 6. 원주 문막 선로망 (여유 풍부)
  {
    id: 'line-wonju-munmak',
    substationId: 'wonju-munmak',
    substationName: '154kV 원주변전소',
    feederName: '문막 D/L',
    voltage: '22.9kV-y',
    status: 'ABUNDANT',
    remainingKw: 1900,
    capacityKw: 10000,
    path: [
      [37.3450, 127.9250],
      [37.3380, 127.9150],
      [37.3290, 127.9020],
      [37.3180, 127.8890], // 문막일반산업단지
    ],
  },

  // 7. 당진 송악 선로망 (포화 마감)
  {
    id: 'line-dangjin-songak',
    substationId: 'dangjin-songak',
    substationName: '154kV 송악변전소',
    feederName: '송악 D/L',
    voltage: '22.9kV-y',
    status: 'SATURATED',
    remainingKw: 0,
    capacityKw: 10000,
    path: [
      [36.9150, 126.6950],
      [36.9250, 126.6850],
      [36.9380, 126.6720],
      [36.9510, 126.6590], // 송악 현대제철 배후철강단지
    ],
  },

  // 8. 구미 옥계 선로망 (여유 풍부)
  {
    id: 'line-gumi-okgye',
    substationId: 'gumi-okgye',
    substationName: '154kV 옥계변전소',
    feederName: '산동 D/L',
    voltage: '22.9kV-y',
    status: 'ABUNDANT',
    remainingKw: 1400,
    capacityKw: 10000,
    path: [
      [36.1450, 128.4150],
      [36.1520, 128.4280],
      [36.1610, 128.4410],
      [36.1720, 128.4550], // 구미 4단지 및 5단지 하이테크밸리
    ],
  },
];

// =========================================================================
// 3. 전국 핵심 산업단지 & 지붕 타깃 클러스터 DB
// =========================================================================
export const TARGET_INDUSTRIAL_PARKS: TargetIndustrialPark[] = [
  // 서울권역 지식산업/물류/R&D 지붕 타깃
  {
    id: 'park-seoul-gvalley',
    name: 'G밸리 (서울디지털국가산업단지 1·2·3단지)',
    city: '금천구',
    town: '가산동',
    substationId: 'seoul-gasan',
    connectedFeeder: '디지털 D/L',
    lat: 37.4880,
    lng: 126.8920,
    estimatedRoofPyung: 185000,
    potentialKw: 92500,
    factoryCount: 450,
    status: 'ABUNDANT',
    targetType: '지식산업센터',
  },
  {
    id: 'park-seoul-munjeong',
    name: '동남권 서울복합물류단지 & 가든파이브',
    city: '송파구',
    town: '장지동',
    substationId: 'seoul-munjeong',
    connectedFeeder: '물류 D/L',
    lat: 37.4860,
    lng: 127.1400,
    estimatedRoofPyung: 220000,
    potentialKw: 110000,
    factoryCount: 85,
    status: 'ABUNDANT',
    targetType: '물류센터',
  },
  {
    id: 'park-seoul-seongsu',
    name: '성수 IT지식산업밸리 옥상 클러스터',
    city: '성동구',
    town: '성수동',
    substationId: 'seoul-seongsu',
    connectedFeeder: '성수 D/L',
    lat: 37.5415,
    lng: 127.0560,
    estimatedRoofPyung: 120000,
    potentialKw: 60000,
    factoryCount: 160,
    status: 'NORMAL',
    targetType: '지식산업센터',
  },
  {
    id: 'park-seoul-magok',
    name: '마곡 R&D 산업단지 옥상 클러스터',
    city: '강서구',
    town: '마곡동',
    substationId: 'seoul-magok',
    connectedFeeder: '마곡 D/L',
    lat: 37.5610,
    lng: 126.8320,
    estimatedRoofPyung: 160000,
    potentialKw: 80000,
    factoryCount: 110,
    status: 'ABUNDANT',
    targetType: '지식산업센터',
  },

  // 수도권 / 경기 / 지방 권역
  {
    id: 'park-hwaseong-yulam',
    name: '팔탄 율암온천 배후 공장 클러스터',
    city: '화성시',
    town: '팔탄면',
    substationId: 'hwaseong-paltan',
    connectedFeeder: '율암 D/L',
    lat: 37.1410,
    lng: 126.8720,
    estimatedRoofPyung: 85000,
    potentialKw: 42500,
    factoryCount: 128,
    status: 'SATURATED',
    targetType: '공장단지',
  },
  {
    id: 'park-hwaseong-hyangnam',
    name: '향남 제약국가산업단지',
    city: '화성시',
    town: '향남읍',
    substationId: 'hwaseong-hyangnam',
    connectedFeeder: '발안 D/L',
    lat: 37.1150,
    lng: 126.8970,
    estimatedRoofPyung: 140000,
    potentialKw: 70000,
    factoryCount: 65,
    status: 'SATURATED',
    targetType: '국가산단',
  },
  {
    id: 'park-pyeongtaek-poseung',
    name: '포승국가산업단지 / 평택항 물류클러스터',
    city: '평택시',
    town: '포승읍',
    substationId: 'pyeongtaek-poseung',
    connectedFeeder: '포승 D/L',
    lat: 36.9650,
    lng: 126.8290,
    estimatedRoofPyung: 320000,
    potentialKw: 160000,
    factoryCount: 210,
    status: 'SATURATED',
    targetType: '국가산단',
  },
  {
    id: 'park-icheon-moga',
    name: '이천 모가·부발 물류센터 클러스터',
    city: '이천시',
    town: '모가면',
    substationId: 'icheon-bubal',
    connectedFeeder: '모가 D/L',
    lat: 37.2300,
    lng: 127.4580,
    estimatedRoofPyung: 180000,
    potentialKw: 90000,
    factoryCount: 42,
    status: 'ABUNDANT',
    targetType: '물류센터',
  },
  {
    id: 'park-pocheon-yongjeong',
    name: '포천 용정일반산단 & 가산가구단지',
    city: '포천시',
    town: '군내면',
    substationId: 'pocheon-gunnae',
    connectedFeeder: '군내 D/L',
    lat: 37.8720,
    lng: 127.1720,
    estimatedRoofPyung: 110000,
    potentialKw: 55000,
    factoryCount: 95,
    status: 'ABUNDANT',
    targetType: '일반산단',
  },
  {
    id: 'park-wonju-munmak',
    name: '문막일반산업단지 & 자동차부품밸리',
    city: '원주시',
    town: '문막읍',
    substationId: 'wonju-munmak',
    connectedFeeder: '문막 D/L',
    lat: 37.3180,
    lng: 127.8890,
    estimatedRoofPyung: 150000,
    potentialKw: 75000,
    factoryCount: 88,
    status: 'ABUNDANT',
    targetType: '일반산단',
  },
  {
    id: 'park-dangjin-songak',
    name: '당진 송악 철강산업클러스터 (현대제철 배후)',
    city: '당진시',
    town: '송악읍',
    substationId: 'dangjin-songak',
    connectedFeeder: '송악 D/L',
    lat: 36.9510,
    lng: 126.6590,
    estimatedRoofPyung: 290000,
    potentialKw: 145000,
    factoryCount: 140,
    status: 'SATURATED',
    targetType: '국가산단',
  },
  {
    id: 'park-gumi-4th',
    name: '구미국가산업단지 4단지 & 하이테크밸리',
    city: '구미시',
    town: '산동읍',
    substationId: 'gumi-okgye',
    connectedFeeder: '산동 D/L',
    lat: 36.1720,
    lng: 128.4550,
    estimatedRoofPyung: 380000,
    potentialKw: 190000,
    factoryCount: 175,
    status: 'ABUNDANT',
    targetType: '국가산단',
  },
];
